/** Accurate, isolated product context for collection offers. No page scripts execute. */
(function (root, factory) {
  'use strict';
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DSStudioProductContext = api;
})(typeof globalThis === 'object' ? globalThis : this, function (root) {
  'use strict';

  function fail(message) { throw new Error(message); }
  function text(value) { return value == null ? '' : String(value).trim(); }
  function id(value, label) {
    var result = text(value);
    if (!/^[1-9]\d*$/.test(result)) fail((label || 'Product') + ' details could not be verified.');
    return result;
  }
  function cents(value, label) {
    if (value === null || value === '' || typeof value === 'boolean') fail((label || 'Price') + ' is unavailable.');
    var result = Number(value);
    if (!Number.isSafeInteger(result) || result < 0) fail((label || 'Price') + ' is unavailable.');
    return result;
  }
  function currency(value) {
    var result = text(value).toUpperCase();
    if (!/^[A-Z]{3}$/.test(result)) fail('The current currency could not be verified.');
    return result;
  }
  function availability(value, label) {
    if (typeof value !== 'boolean') fail((label || 'Product') + ' availability could not be verified.');
    return value;
  }
  function baseURL(options) {
    var value = options.baseUrl || (root.location && root.location.href);
    if (!value) fail('The current storefront address is unavailable.');
    var url = new URL(value);
    if (!/^https?:$/.test(url.protocol)) fail('The storefront address is invalid.');
    return url;
  }
  function expectedTheme(options, base) {
    // The rendered theme role wins over a bookmarked preview URL after launch.
    if (options.preview === false) return '';
    var value = options.previewThemeId || options.themeId || (base && base.searchParams.get('preview_theme_id'));
    return value ? id(value, 'Preview theme') : '';
  }
  function productURL(card, options) {
    options = options || {};
    var base = baseURL(options), url;
    try { url = new URL(text(card && card.url), base); } catch (_) { fail('This product link is unavailable.'); }
    if (url.origin !== base.origin || !/^https?:$/.test(url.protocol) || !/\/products\/[^/]+\/?$/.test(url.pathname)) {
      fail('This product link does not belong to the current storefront.');
    }
    var locale = text(options.rootUrl || '/');
    var localeURL = new URL(locale, base);
    if (localeURL.origin !== base.origin || localeURL.search || localeURL.hash) fail('The storefront language route is invalid.');
    var prefix = localeURL.pathname.replace(/\/$/, '');
    if (prefix && /^\/products\//.test(url.pathname)) url.pathname = prefix + url.pathname;
    var themeId = expectedTheme(options, base);
    if (themeId) url.searchParams.set('preview_theme_id', themeId);
    else url.searchParams.delete('preview_theme_id');
    ['_ab', '_fd', '_sc'].forEach(function (key) {
      if (base.searchParams.has(key)) url.searchParams.set(key, base.searchParams.get(key));
    });
    url.searchParams.delete('intent');
    url.searchParams.delete('variant');
    url.searchParams.set('view', 'ds-release');
    url.hash = '';
    return url;
  }
  function mediaURL(value, base) {
    if (!value) return '';
    var url;
    try { url = new URL(String(value), base); } catch (_) { fail('A product photo or video could not be loaded.'); }
    if (!/^https?:$/.test(url.protocol)) fail('A product photo or video has an unsupported address.');
    return url.href;
  }
  function nativeValue(value) {
    return text(value && typeof value === 'object' ? (value.value == null ? value.name : value.value) : value);
  }
  function readModel(config, options) {
    var modelAPI = options.model || root.DSProductModel;
    if (!modelAPI || typeof modelAPI.build !== 'function') {
      var product = config.productData, kind = '';
      var sources = [product.category && product.category.name, product.type, product.tags && (Array.isArray(product.tags) ? product.tags.join(' ') : product.tags), product.handle, product.title];
      sources.some(function (source) {
        var hint = text(source).toLowerCase().replace(/[_-]+/g, ' ');
        if (/\bearrings?\b/.test(hint)) kind = 'earrings';
        else if (/\b(?:necklaces?|pendants?)\b/.test(hint)) kind = 'necklaces';
        else if (/\b(?:bracelets?|bangles?)\b/.test(hint)) kind = 'bracelets';
        else if (/\bbridal\b/.test(hint)) kind = 'bridal';
        else if (/\bbands?\b/.test(hint)) kind = 'bands';
        else if (/\brings?\b/.test(hint)) kind = 'rings';
        return Boolean(kind);
      });
      var labels = {ring_style: 'Ring style', main_diamond_shape: 'Main diamond shape', main_diamond_carat_weight: 'Main diamond weight (listed)', total_carat_weight: 'Total diamond weight (listed)', main_diamond_color: 'Main diamond color', main_diamond_clarity: 'Main diamond clarity', certification: 'Certification'};
      var items = Object.keys(labels).map(function (key) {
        var value = config.metafields && config.metafields[key];
        return {key: key, label: labels[key], value: typeof value === 'string' || typeof value === 'number' ? text(value) : ''};
      }).filter(function (item) { return item.value && !/^(?:n\/?a|none|null|undefined|unknown|[-–—]+)$/i.test(item.value); });
      return {category: {id: kind || 'jewelry', isRing: ['rings', 'bridal', 'bands'].indexOf(kind) !== -1}, fields: items.length ? [{id: 'product', label: 'Product details', items: items}] : []};
    }
    var model = modelAPI.build(config);
    if (!model || !model.category || typeof model.category.isRing !== 'boolean') fail('The product type could not be verified.');
    return model;
  }
  function normalize(card, config, options) {
    options = options || {};
    if (!card || !config || !config.productData) fail('The selected product details are unavailable.');
    var product = config.productData;
    var productId = id(product.id);
    if (productId !== id(card.id)) fail('The loaded details do not match the selected piece. Please try again.');
    var title = text(product.title);
    if (!title) fail('The selected product title is unavailable.');
    var actualCurrency = currency(config.priceCurrency || config.currency);
    if (config.priceCurrency && config.currency && currency(config.currency) !== actualCurrency) fail('The product currency changed. Please reload the collection.');
    if (currency(card.currency) !== actualCurrency || (options.currency && currency(options.currency) !== actualCurrency)) {
      fail('The product currency changed. Please reload the collection.');
    }
    var base = options.baseUrl || (root.location && root.location.href) || 'https://diamondstudio.com/';
    var expected = expectedTheme(options, new URL(base));
    if (expected && (id(config.themeId, 'Preview theme') !== expected || config.isPreview !== true)) {
      fail('The product did not load from this preview theme. Please try again.');
    }
    var country = text(config.country).toUpperCase();
    if (!/^[A-Z]{2}$/.test(country)) fail('Your shopping region could not be verified.');
    if (options.country && text(options.country).toUpperCase() !== country) fail('Your shopping region changed. Please reload the collection.');
    if (!Array.isArray(config.images) || !Array.isArray(config.media) || !Array.isArray(config.options) || !Array.isArray(config.variants)) {
      fail('Complete product photos and options are unavailable. Please open the full product details.');
    }
    var model = readModel(config, options);
    var rawOptions = config.options.map(function (option) {
      if (!option || !text(option.name) || !Array.isArray(option.values) || !option.values.length) fail('Product option details are incomplete.');
      var values = option.values.map(nativeValue);
      if (values.some(function (value) { return !value; }) || new Set(values).size !== values.length) fail('Product option details are incomplete.');
      return {name: text(option.name), values: values};
    });
    var onlyDefaultTitle = rawOptions.length === 1 && rawOptions[0].name === 'Title' && rawOptions[0].values.length === 1 && rawOptions[0].values[0] === 'Default Title';
    var nativeOptions = onlyDefaultTitle ? [] : rawOptions;
    var seenIds = new Set();
    var variants = config.variants.map(function (variant) {
      var variantId = id(variant && variant.id, 'Variant');
      if (seenIds.has(variantId)) fail('Product variants could not be verified.');
      seenIds.add(variantId);
      var values = Array.isArray(variant.options) ? variant.options.map(nativeValue) : rawOptions.map(function (_, index) { return nativeValue(variant['option' + (index + 1)]); });
      if (values.length !== rawOptions.length || values.some(function (value, index) { return rawOptions[index].values.indexOf(value) === -1; })) {
        fail('Product variants and options do not match.');
      }
      return {
        id: variantId,
        title: text(variant.title),
        options: onlyDefaultTitle ? [] : values,
        price: cents(variant.price, 'Variant price'),
        available: availability(variant.available, 'Variant'),
        featuredImageId: variant.featured_image && variant.featured_image.id ? text(variant.featured_image.id) : '',
        featuredImage: variant.featured_image && variant.featured_image.src ? mediaURL(variant.featured_image.src, base) : ''
      };
    });
    if (!variants.length) fail('No product variants are available to verify.');
    if (!nativeOptions.length && variants.length !== 1) fail('This product needs additional option details.');
    var images = config.images.map(function (image) {
      if (!image || !image.src) fail('The complete product photo gallery is unavailable.');
      return {id: text(image.id), src: mediaURL(image.src, base), alt: text(image.alt) || title};
    });
    var videos = config.media.filter(function (item) { return item && (item.media_type === 'video' || item.media_type === 'external_video'); }).map(function (item) {
      var poster = mediaURL(item.preview_image && (item.preview_image.src || item.preview_image.url), base);
      if (item.media_type === 'video') {
        var sources = (Array.isArray(item.sources) ? item.sources : []).map(function (source) {
          return {src: mediaURL(source.url || source.src, base), type: text(source.mime_type || source.format), width: Number(source.width) || null, height: Number(source.height) || null};
        }).filter(function (source) { return source.src; });
        if (!sources.length) fail('A product video is unavailable.');
        var preferred = sources.find(function (source) { return source.type === 'video/mp4' || source.type === 'mp4'; }) || sources[0];
        return {id: text(item.id), type: 'video', src: preferred.src, sources: sources, poster: poster, alt: text(item.alt) || title};
      }
      var host = text(item.host).toLowerCase(), externalId = text(item.external_id), external;
      if (host === 'youtube' && /^[\w-]+$/.test(externalId)) external = 'https://www.youtube-nocookie.com/embed/' + externalId;
      else if (host === 'vimeo' && /^\d+$/.test(externalId)) external = 'https://player.vimeo.com/video/' + externalId;
      else fail('This product video needs to be viewed on the full product page.');
      return {id: text(item.id), type: 'external_video', host: host, externalId: externalId, src: external, poster: poster, alt: text(item.alt) || title};
    });
    if (!Array.isArray(card.gold)) fail('The available gold options could not be verified.');
    if (card.gold.length && card.goldSource !== 'globo_option.option_302218') fail('This product uses options that need studio assistance.');
    var addonVariants = config.addonProduct && config.addonProduct.variants;
    var gold = card.gold.map(function (choice) {
      if (!choice || !text(choice.label)) fail('Gold option details are incomplete.');
      var extra = cents(choice.extra, 'Gold upgrade price');
      var available = availability(choice.available, 'Gold option');
      var addonId = choice.addonVariantId == null || choice.addonVariantId === '' ? null : id(choice.addonVariantId, 'Gold upgrade');
      if (addonId) {
        if (!Array.isArray(addonVariants)) fail('Gold upgrade details are still loading.');
        var addon = addonVariants.find(function (variant) { return text(variant.id) === addonId; });
        if (!addon || cents(addon.price, 'Gold upgrade price') !== extra) fail('A gold upgrade price changed. Please reload the collection.');
        available = available && availability(addon.available, 'Gold upgrade');
      } else if (extra !== 0) fail('This gold upgrade could not be verified.');
      return {label: text(choice.label), available: available, extra: extra, addonVariantId: addonId};
    });
    var nativeSizeIndex = model.category.isRing ? nativeOptions.findIndex(function (option) { return /^(?:ring[ _-]*)?size$/i.test(option.name); }) : -1;
    var groups = (model.fields || []).map(function (group) {
      return {id: text(group.id), label: text(group.label), items: (group.items || []).map(function (item) { return {key: text(item.key), label: text(item.label || item.key), value: text(item.value)}; }).filter(function (item) { return item.label && item.value; })};
    }).filter(function (group) { return group.items.length; });
    return {
      id: productId, productId: productId, title: title, handle: text(product.handle),
      url: text(options.productUrl || card.url), currency: actualCurrency, country: country,
      themeId: text(config.themeId), rootUrl: text(config.rootUrl || options.rootUrl),
      category: model.category, images: images, videos: videos, media: config.media,
      specs: groups.reduce(function (items, group) { return items.concat(group.items); }, []), specGroups: groups,
      nativeOptions: nativeOptions, variants: variants, gold: gold,
      nativeSizeIndex: nativeSizeIndex, customRingSize: model.category.isRing && nativeSizeIndex < 0,
      selectedVariantId: config.selectedVariant && config.selectedVariant.id ? text(config.selectedVariant.id) : '',
      available: variants.some(function (variant) { return variant.available; })
    };
  }
  function resolveVariant(context, selectedValues) {
    if (!context || !Array.isArray(context.nativeOptions) || !Array.isArray(context.variants)) fail('Product options are still loading.');
    if (!Array.isArray(selectedValues) || selectedValues.length !== context.nativeOptions.length) fail('Choose each product option before continuing.');
    var values = selectedValues.map(nativeValue);
    context.nativeOptions.forEach(function (option, index) {
      if (!values[index] || option.values.indexOf(values[index]) === -1) fail('Choose an available ' + option.name.toLowerCase() + '.');
    });
    var matches = context.variants.filter(function (variant) {
      return variant.options.length === values.length && variant.options.every(function (value, index) { return value === values[index]; });
    });
    if (matches.length !== 1) fail('This combination of product options is unavailable.');
    var variant = matches[0];
    if (!variant.available) fail('Choose an available product option before making an offer.');
    return variant;
  }
  function resolveSelection(context, selection) {
    if (!context || !Array.isArray(context.gold)) fail('Product options are still loading.');
    selection = selection || {};
    var variant = resolveVariant(context, selection.nativeOptions);
    var values = selection.nativeOptions.map(nativeValue);
    var gold = null;
    if (context.gold.length) {
      if (!/^(?:0|[1-9]\d*)$/.test(String(selection.goldIndex))) fail('Choose your gold before making an offer.');
      gold = context.gold[Number(selection.goldIndex)];
      if (!gold || !gold.available) fail('Choose an available gold option.');
    } else if (selection.goldIndex != null && selection.goldIndex !== '' && Number(selection.goldIndex) !== -1) {
      fail('Gold choices are unavailable for this piece.');
    }
    var size = '';
    if (context.customRingSize) {
      size = text(selection.size);
      if (!/^(?:[3-9](?:\.5)?|10(?:\.5)?|11)$/.test(size)) fail('Choose your US ring size from 3 to 11.');
    } else if (context.nativeSizeIndex >= 0) size = values[context.nativeSizeIndex];
    var total = cents(variant.price, 'Variant price') + (gold ? cents(gold.extra, 'Gold upgrade price') : 0);
    if (!Number.isSafeInteger(total)) fail('The selected price could not be verified.');
    return {
      productId: id(context.id), variantId: id(variant.id, 'Variant'),
      nativeOptions: context.nativeOptions.map(function (option, index) { return {name: option.name, value: values[index]}; }),
      gold: gold ? gold.label : '', ringSize: size, addonVariantId: gold ? gold.addonVariantId : null,
      priceCents: total, displayedTotalCents: total, currency: context.currency, available: true
    };
  }
  async function load(card, options) {
    options = options || {};
    var url = productURL(card, options);
    var request = options.fetch || (root.fetch && root.fetch.bind(root));
    if (typeof request !== 'function' || typeof root.DOMParser !== 'function') fail('Your browser could not load the product details. Please open the full product page.');
    var response = await request(url.href, {credentials: 'same-origin', signal: options.signal, headers: {Accept: 'text/html'}});
    if (!response.ok) fail('The ring details could not load. Please try again.');
    if (response.url && new URL(response.url).origin !== url.origin) fail('The product request left the current storefront.');
    var html = await response.text();
    if (options.signal && options.signal.aborted) {
      var aborted = new Error('The product request was cancelled.'); aborted.name = 'AbortError'; throw aborted;
    }
    var document = new root.DOMParser().parseFromString(html, 'text/html');
    var nodes = document.querySelectorAll('script#ds-preview-config[type="application/json"]');
    if (nodes.length !== 1) fail('Complete product details are unavailable. Please open the full product page.');
    var config;
    try { config = JSON.parse(nodes[0].textContent); } catch (_) { fail('The product details could not be read. Please try again.'); }
    return normalize(card, config, Object.assign({}, options, {baseUrl: url.href, productUrl: url.href}));
  }
  return {load: load, normalize: normalize, resolveVariant: resolveVariant, resolveSelection: resolveSelection, productURL: productURL};
});
